// Client-side token renderer for the contract-sections document.
//
// A function-for-function port of the server engine (diabolical
// app/Diabolical/ContractTokens.php), so a token renders identically
// client-side and server-side. Paths and modifiers are resolved against the
// shared catalog owned by the FE (tokenCatalog.json); the server reads the
// same catalog from the deployed FE build, so the single source of truth is
// retained. Token syntax is {{ path (: modifier (: args)) }}.
//
// Value sources are supplied by a TokenContext: the campaign context resolves
// against the campaign's form values, the template context renders placeholder
// values (no campaign data exists there).
import rawCatalog from './tokenCatalog.json';
import { TOKEN_REGEX } from '../htmlTokenSchema';

export type CatalogItemField = {
    path: string,
    label?: string,
    type?: 'date' | 'money' | 'string' | 'number' | null,
    category?: string,
};

export type CatalogField = {
    path: string,
    label?: string,
    type?: 'date' | 'money' | 'string' | 'number' | 'collection' | null,
    category?: string,
    itemFields?: CatalogItemField[],
};

// The shared FE-owned catalog. Edit the JSON, not a copy.
export const CATALOG: CatalogField[] = rawCatalog as unknown as CatalogField[];

// A value source for token rendering. The campaign context resolves against
// the campaign's form values; the template context short-circuits every
// resolvable path to a placeholder via placeholderFor.
export interface TokenContext {
    // Raw value for a scalar catalog path (string or number, pence for money).
    valueFor(path: string): string | number | null,
    // ISO date (YYYY-MM-DD) for a date catalog path.
    dateFor(path: string): string | null,
    // Collection items keyed by item-field path.
    collectionItems(path: string): Record<string, string | null>[],
    // Currency glyph for money formatting.
    currencyGlyph(): string,
    // When set (template context), the placeholder for a resolvable path,
    // including collection and item paths; undefined in the campaign context.
    placeholderFor?(path: string): string | null,
}

// Resolves a dot-path against the catalog. Returns { field, itemPath }:
// an exact field match (e.g. totals.area_booking, panels) or a collection
// item path (e.g. panels.site.name -> field "panels", item "site.name").
export function resolvePath(path: string, catalog: CatalogField[] = CATALOG): { field: CatalogField, itemPath: string | null } | null {
    for (const field of catalog) {
        if (field.path === path) {
            return { field, itemPath: null };
        }
    }
    for (const field of catalog) {
        if (field.type !== 'collection') {
            continue;
        }
        const prefix = field.path + '.';
        if (path.startsWith(prefix) && (field.itemFields ?? []).some(f => f.path === path.slice(prefix.length))) {
            return { field, itemPath: path.slice(prefix.length) };
        }
    }
    return null;
}

function itemFieldType(field: CatalogField, itemPath: string): string {
    for (const item of field.itemFields ?? []) {
        if (item.path === itemPath) {
            return item.type ?? 'string';
        }
    }
    return 'string';
}

// Table columns: the args list of item-field paths, defaulting to all item fields.
export function tableColumns(field: CatalogField, args: string): string[] {
    const all = (field.itemFields ?? []).map(f => f.path);
    if (args.trim() === '') {
        return all;
    }
    const wanted = args.split(',').map(s => s.trim()).filter(s => s !== '');
    // unknown paths are dropped rather than rendered blank
    return wanted.filter(w => all.includes(w));
}

function escape(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function renderTable(field: CatalogField, columns: string[], items: Record<string, string | null>[]): string {
    const labels: Record<string, string> = {};
    for (const item of field.itemFields ?? []) {
        labels[item.path] = item.label ?? item.path;
    }
    const head = '<tr>' + columns.map(col => `<th>${escape(labels[col] ?? col)}</th>`).join('') + '</tr>';
    const rows = items.map(item => {
        const cells = columns.map(col => {
            const value = col in item ? item[col] : null;
            return '<td>' + ((value === null || value === '') ? '' : escape(value)) + '</td>';
        });
        return '<tr>' + cells.join('') + '</tr>';
    });
    return '<table class="contract-table"><thead>' + head + '</thead><tbody>' + rows.join('') + '</tbody></table>';
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Parses an ISO date (YYYY-MM-DD) as a UTC date, so formatting is
// timezone-independent (the server's Carbon parses the same date parts).
export function parseIsoDate(value: string | null): Date | null {
    if (!value) {
        return null;
    }
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!m) {
        return null;
    }
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function ordinal(day: number): string {
    if (day % 100 >= 11 && day % 100 <= 13) {
        return day + 'th';
    }
    switch (day % 10) {
        case 1: return day + 'st';
        case 2: return day + 'nd';
        case 3: return day + 'rd';
        default: return day + 'th';
    }
}

// Formats a date per its modifier (port of ContractTokens::formatDate).
export function formatDate(date: Date | null, modifier: string): string {
    if (date === null) {
        return '';
    }
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    const pad = (n: number) => String(n).padStart(2, '0');
    switch (modifier === '' ? 'full' : modifier) {
        case 'iso':
            return `${year}-${pad(month + 1)}-${pad(day)}`;
        case 'year':
            return String(year);
        case 'month':
            return MONTHS[month];
        case 'day':
            return String(day);
        case 'dmy':
            return `${pad(day)}/${pad(month + 1)}/${year}`;
        case 'weekday':
            return WEEKDAYS[date.getUTCDay()];
        case 'weekday_ordinal_month':
            return `${WEEKDAYS[date.getUTCDay()]} ${ordinal(day)} ${MONTHS[month]} ${year}`;
        case 'full':
        default:
            return `${day} ${MONTHS[month]} ${year}`;
    }
}

// Formats a money value stored in pence (port of ContractTokens::formatMoney).
export function formatMoney(pence: number, modifier: string, glyph: string): string {
    const amount = pence / 100;
    const withCommas = (decimals: number) => {
        const [int, frac] = amount.toFixed(decimals).split('.');
        return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (frac !== undefined ? '.' + frac : '');
    };
    switch (modifier === '' ? 'currency' : modifier) {
        case 'comma':
            return withCommas(2);
        case 'plain':
            return amount.toFixed(2);
        case 'percent':
            return Math.round(amount).toString() + '%';
        case 'currency':
        default:
            return glyph + withCommas(2);
    }
}

// Joins collection values per the join modifier (port of ContractTokens::joinValues).
export function joinValues(values: string[], join: string): string {
    switch (join) {
        case 'linebreak':
            return values.join('<br>');
        case 'space':
            return values.join(' ');
        case 'bullet':
            return '<ul>' + values.map(v => `<li>${v}</li>`).join('') + '</ul>';
        case 'semicolon':
            return values.join('; ');
        case 'comma':
        default:
            return values.join(', ');
    }
}

// Heading-clamp pass over resolved block content (design D4): the document's
// own h1/h2/h3 are reserved for the title, section titles and block titles,
// so content headings render two levels deeper.
// h1->h3, h2->h4, h3->h5, h4-or-deeper->h6.
export function clampHeadings(html: string): string {
    return html.replace(/<\/?h([1-6])(\s[^>]*|>)/gi, (m, level, rest) => {
        const n = Number(level);
        return (m[1] === '/' ? '</h' : '<h') + (n >= 4 ? 6 : n + 2) + rest;
    });
}

// --- Artwork selection (design D2) -------------------------------------------

// One index part: digits, optionally an inclusive range dash.
const INDEX_PART = /^(\d+)(?:-(\d+))?$/;

// Expands the index-shaped parts of an args string into 1-based indices
// (inclusive ranges); null when the arg carries no index parts.
function parseArtworkIndices(arg: string): number[] | null {
    const indices: number[] = [];
    let any = false;
    for (const part of arg.split(',').map(s => s.trim()).filter(s => s !== '')) {
        const m = INDEX_PART.exec(part);
        if (m !== null) {
            any = true;
            const lo = Number(m[1]);
            const hi = m[2] !== undefined ? Number(m[2]) : lo;
            for (let i = lo; i <= hi; i++) {
                indices.push(i);
            }
        }
    }
    return any ? indices : null;
}

// The artwork selection grammar (design D2): an index-shaped part selects
// indices; a non-index part is resolved in order - exact distributor-name match
// (case-insensitive) -> distributor filter, else exact original-name match
// (case-insensitive) -> that single file, else no files. The name filter is
// applied first, then the index selection to the filtered list; out-of-range
// indices are dropped.
function selectArtworkItems(items: Record<string, string | null>[], args: string): Record<string, string | null>[] {
    const arg = args.trim();
    let selected = items;
    if (arg !== '') {
        const parts = arg.split(',').map(s => s.trim()).filter(s => s !== '');
        const namePart = parts.find(p => !INDEX_PART.test(p));
        if (namePart !== undefined) {
            const lower = namePart.toLowerCase();
            const matchesDistributor = items.some(item => (item.distributor ?? '').toLowerCase() === lower);
            selected = items.filter(item =>
                matchesDistributor
                    ? (item.distributor ?? '').toLowerCase() === lower
                    : (item.original_name ?? '').toLowerCase() === lower
            );
        }
        const indices = parseArtworkIndices(arg);
        if (indices !== null) {
            selected = indices.filter(i => i >= 1 && i <= selected.length).map(i => selected[i - 1]);
        }
    }
    return selected;
}

// Formats a single collection-item value per its item-field type.
function formatItemValue(value: string | null, type: string, modifier: string, ctx: TokenContext): string {
    if (type === 'date' && value) {
        return formatDate(parseIsoDate(value), modifier);
    }
    if (type === 'money') {
        return formatMoney((value ?? 0) as number, modifier, ctx.currencyGlyph());
    }
    return value === null ? '' : String(value);
}

// Renders one token (port of ContractTokens::renderToken).
export function renderToken(path: string, modifier: string, args: string, ctx: TokenContext, catalog: CatalogField[] = CATALOG): string {
    const resolved = resolvePath(path, catalog);
    if (resolved === null) {
        return `<span class="unresolved-token">[unresolved: ${path}]</span>`;
    }
    const { field, itemPath } = resolved;

    const placeholder = ctx.placeholderFor?.(path);
    if (placeholder) {
        return placeholder;
    }

    if (field.type === 'collection') {
        const items = ctx.collectionItems(field.path);
        if (modifier === 'table') {
            return renderTable(field, tableColumns(field, args), items);
        }
        if (field.path === 'artwork' && (modifier === 'image' || modifier === 'images')) {
            const selected = selectArtworkItems(items, args);
            const files = modifier === 'image' ? selected.slice(0, 1) : selected;
            return files.map(f => `<img class="contract-artwork" src="${escape(f.url ?? '')}" alt="${escape(f.original_name ?? '')}">`).join('');
        }
        const values = items.map(item => formatItemValue(
            itemPath !== null && itemPath in item ? item[itemPath] : null,
            itemFieldType(field, itemPath ?? ''),
            modifier,
            ctx
        ));
        return joinValues(values, modifier === '' ? 'comma' : modifier);
    }

    const type = field.type ?? 'string';
    if (type === 'date') {
        return formatDate(parseIsoDate(ctx.dateFor(path)), modifier);
    }
    if (type === 'money') {
        return formatMoney((ctx.valueFor(path) ?? 0) as number, modifier, ctx.currencyGlyph());
    }

    const value = ctx.valueFor(path);
    // Newlines render as line breaks (the address token is the first consumer);
    // mirrors the server's str_replace pass.
    return value === null ? '' : String(value).replace(/\n/g, '<br>');
}

// Renders every token in $html against the context; unknown paths stay visible.
export function render(html: string, ctx: TokenContext, catalog: CatalogField[] = CATALOG): string {
    return html.replace(TOKEN_REGEX, (m, path, modifier, args) =>
        renderToken(path.trim(), modifier !== undefined ? modifier.trim() : '', args ?? '', ctx, catalog));
}

// --- Contexts ----------------------------------------------------------------

// A campaign-shaped value source (structural: the generated Campaign type is
// assignable). Fields the campaign query does not select resolve to null and
// render with the server's null handling (money -> zero, string -> empty).
export type CampaignTokenSource = {
    name?: string | null,
    job_reference?: string | null,
    sales_job_ref?: string | null,
    event?: string | null,
    start_date?: string | null,
    completion_date?: string | null,
    total_price?: number | null,
    total_price_with_vat?: number | null,
    terms?: string | null,
    totals?: Record<string, number | null | string | undefined> | null,
    currency?: { glyph?: string | null } | null,
    clientCompany?: { accounts_company?: string | null, sales_company?: string | null } | null,
    clientCompanyContact?: { name?: string | null } | null,
    salesTeam?: {
        name?: string | null,
        trading_name?: string | null,
        contact_email?: string | null,
        url?: string | null,
        tel?: string | null,
        street_address?: string | null,
        city?: string | null,
        postal_code?: string | null,
        manager?: { name?: string | null } | null,
    } | null,
    ourContact?: { name?: string | null, email?: string | null } | null,
    paymentTerms?: { name?: string | null } | null,
    panelBookings?: {
        panel?: {
            panel?: string | null,
            site?: {
                name?: string | null,
                borough?: { label?: string | null } | null,
                region?: { name?: string | null } | null,
            } | null,
        } | null,
    }[] | null,
    creativeStudioEntries?: {
        name?: string | null,
        start_date?: string | null,
        end_date?: string | null,
    }[] | null,
    artworkFiles?: {
        url?: string | null,
        original_name?: string | null,
        uploaded?: string | null,
        distributor?: { name?: string | null } | null,
    }[] | null,
};

// The campaign context: resolves tokens against the campaign's form values.
export function campaignTokenContext(campaign: CampaignTokenSource): TokenContext {
    return {
        valueFor: (path: string): string | number | null => {
            switch (path) {
                case 'name': return campaign.name ?? null;
                case 'job_reference': return campaign.job_reference ?? null;
                case 'sales_job_ref': return campaign.sales_job_ref ?? null;
                case 'event': return campaign.event ?? null;
                case 'client_company': return campaign.clientCompany?.accounts_company || campaign.clientCompany?.sales_company || null;
                case 'client_company.trading_name': return campaign.clientCompany?.sales_company || campaign.clientCompany?.accounts_company || null;
                case 'client_contact': return campaign.clientCompanyContact?.name ?? null;
                case 'sales_team.name': return campaign.salesTeam?.name ?? null;
                case 'sales_team.trading_name': return campaign.salesTeam?.trading_name ?? null;
                case 'sales_team.contact_email': return campaign.salesTeam?.contact_email ?? null;
                case 'sales_team.url': return campaign.salesTeam?.url ?? null;
                case 'sales_team.tel': return campaign.salesTeam?.tel ?? null;
                case 'sales_team.manager_name': return campaign.salesTeam?.manager?.name ?? null;
                case 'sales_team.address': {
                    // Composed: street / city / postcode, one per line; empty parts are
                    // skipped (the \n -> <br> pass in the string branch renders the
                    // breaks).
                    const st = campaign.salesTeam;
                    if (!st) {
                        return null;
                    }
                    const parts = [st.street_address, st.city, st.postal_code]
                        .map(p => (p ?? '').trim())
                        .filter(p => p !== '');
                    return parts.length > 0 ? parts.join('\n') : null;
                }
                case 'our_contact.name': return campaign.ourContact?.name ?? null;
                case 'our_contact.email': return campaign.ourContact?.email ?? null;
                case 'payment_terms': return campaign.paymentTerms?.name ?? null;
                case 'total_price': return campaign.total_price ?? null;
                case 'total_price_with_vat': return campaign.total_price_with_vat ?? null;
                case 'terms':
                    // Mirrors the server's valueFor: the terms body is clamped to
                    // the document hierarchy. The campaign form does not currently
                    // load the terms Option, so this resolves to null (empty).
                    return campaign.terms ? clampHeadings(campaign.terms) : null;
            }
            if (path.startsWith('totals.')) {
                return campaign.totals?.[path.slice('totals.'.length)] ?? null;
            }
            return null;
        },
        dateFor: (path: string): string | null => {
            switch (path) {
                case 'start_date': return campaign.start_date ?? null;
                case 'completion_date': return campaign.completion_date ?? null;
            }
            return null;
        },
        collectionItems: (path: string): Record<string, string | null>[] => {
            switch (path) {
                case 'panels':
                    return (campaign.panelBookings ?? []).filter(Boolean).map(pb => ({
                        number: pb?.panel?.panel ?? null,
                        'site.name': pb?.panel?.site?.name ?? null,
                        'site.borough': pb?.panel?.site?.borough?.label ?? null,
                        'site.region': pb?.panel?.site?.region?.name ?? null,
                    }));
                case 'creative_studio_entries':
                    return (campaign.creativeStudioEntries ?? []).filter(Boolean).map(e => ({
                        name: e?.name ?? null,
                        start_date: e?.start_date ?? null,
                        end_date: e?.end_date ?? null,
                    }));
                case 'artwork':
                    // The extra `url` key (not an itemField) feeds the image branch;
                    // table columns and item projections only ever use the itemFields.
                    return (campaign.artworkFiles ?? []).filter(Boolean).map(f => ({
                        original_name: f?.original_name ?? null,
                        uploaded: f?.uploaded ?? null,
                        distributor: f?.distributor?.name ?? null,
                        url: f?.url ?? null,
                    }));
            }
            return [];
        },
        currencyGlyph: (): string => campaign.currency?.glyph ?? '',
    };
}

// The template context: no campaign data exists, so every resolvable path
// renders its catalog label as a placeholder (e.g. [Start Date]).
export function placeholderTokenContext(catalog: CatalogField[] = CATALOG): TokenContext {
    const labelFor = (path: string): string | null => {
        const resolved = resolvePath(path, catalog);
        if (resolved === null) {
            return null;
        }
        const { field, itemPath } = resolved;
        if (itemPath !== null) {
            const item = (field.itemFields ?? []).find(f => f.path === itemPath);
            return item ? `[${item.label ?? item.path}]` : null;
        }
        return `[${field.label ?? field.path}]`;
    };
    return {
        valueFor: (path: string) => labelFor(path),
        dateFor: (path: string) => labelFor(path),
        collectionItems: () => [],
        currencyGlyph: () => '',
        placeholderFor: (path: string) => labelFor(path),
    };
}
